<script setup>
import { ref, watch, computed } from "vue";

const input = ref(null)

defineOptions({
    inheritAttrs: false,
})
const props = defineProps({
    modelValue: null,
    type: {
        type: String,
        default: "date",
    },
    // ensure model uses consistent string value
    valueFormat: {
        type: String,
        default: "YYYY-MM-DD",
    },
    required: {
        type: Boolean,
    },
    errors: {
        type: Object,
        default: {},
    },
    // Optional icons/tooltips to mimic the visual shown
    prefixIcon: {
        type: String,
        default: "bi bi-calendar3",
    },
    prefixTooltip: {
        type: String,
        default: "",
    },
    suffixIcon: {
        type: String,
        default: "",
    },
    suffixTooltip: {
        type: String,
        default: "",
    },
    showErrorIcon: {
        type: Boolean,
        default: true,
    }
});
const emit = defineEmits(["update:modelValue"]);

const handleVisibleChange = (visible) => {
    if (visible) {
        // Manually open the date picker when the user chooses a date.
        input.value.focus();
    }
};

const hasError = computed(() => props.errors && props.errors.$error);

function toYMDString(value) {
    if (!value) return value;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        // If already YYYY-MM-DD at start
        const ymd = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
        if (ymd) return ymd[1];

        // 01/02/2023 (DD/MM/YYYY or MM/DD/YYYY) — assume DD/MM/YYYY as per data
        const dmySlash = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (dmySlash) {
            const day = dmySlash[1];
            const month = dmySlash[2];
            const year = dmySlash[3];
            return `${year}-${month}-${day}`;
        }

        // 01 Feb, 2023
        const dMonY = trimmed.match(/^(\d{2})\s([A-Za-z]{3}),\s(\d{4})$/);
        if (dMonY) {
            const day = dMonY[1];
            const mon = dMonY[2].toLowerCase();
            const year = dMonY[3];
            const map = { jan:'01', feb:'02', mar:'03', apr:'04', may:'05', jun:'06', jul:'07', aug:'08', sep:'09', oct:'10', nov:'11', dec:'12' };
            if (map[mon]) return `${year}-${map[mon]}-${day}`;
        }

        // ISO or other parseable strings
        const d = new Date(trimmed);
        if (!isNaN(d.getTime())) return d.toISOString().slice(0,10);
        return value;
    }
    if (value instanceof Date && !isNaN(value.getTime())) {
        return value.toISOString().slice(0,10);
    }
    return value;
}

// proxy for v-model
const proxyValue = computed({
    get() {
        return toYMDString(props.modelValue);
    },
    set(val) {
        emit('update:modelValue', toYMDString(val));
    },
});

// Ensure we don't reference props before it is defined
watch(() => props.modelValue, (first, second) => {
    if (first && input?.value?.parentElement) {
        input.value.parentElement.classList.add('is-filled');
    }
});
</script>


<template>
    <div class="form-group mb-1">
        <label class="form-label " :for="$attrs.id" v-if="$attrs.label"> {{ $attrs.label }}
        <span v-if="required" class="text-black astric">*</span>
        </label>

        <div class="date-with-icon" :class="{ 'has-error': hasError }">
            <span class="prefix-icon" :title="prefixTooltip" v-if="prefixIcon">
                <i :class="prefixIcon"></i>
            </span>

            <el-date-picker v-model="proxyValue" :type="type" :value-format="valueFormat" :format="valueFormat" :id="$attrs.id" :name="$attrs.name" class="w-100"
                :placeholder="$attrs.placeholder || 'Pick a day'" :disabled="$attrs.disabled"
                :readonly="$attrs.readonly" :disabled-date="$attrs['disabled-date']" size="large" placement="bottom"/>

            <!-- <span v-if="hasError && showErrorIcon" class="suffix-icon error-icon"
                :title="errors?.$errors?.[0]?.$message || ''">
                !
            </span>
            <span v-else-if="suffixIcon" class="suffix-icon" :title="suffixTooltip">
                <i :class="suffixIcon"></i>
            </span> -->
        </div>

        <small class="text-danger" style="font-size: 0.8rem; top: 40px;" v-if="errors.$error">
            {{ errors.$errors[0].$message }}
        </small>
    </div>

</template>

<style scoped>
.date-with-icon {
    position: relative;
}

/* Increase left padding on the inner Element Plus input */
.date-with-icon :deep(.el-input__wrapper) {
    border-radius: 0.5rem;
    height: 48px;
    /* match bootstrap form-control height */
}

.date-with-icon :deep(.el-input),
.date-with-icon :deep(.el-input__wrapper),
.date-with-icon :deep(.el-input__inner) {
    height: 48px;
}

.date-with-icon :deep(.el-input__inner) {
    line-height: 48px;
}

.prefix-icon {
    position: absolute;
    left: 0.6rem;
    top: 50%;
    transform: translateY(-50%);
    color: #6c757d;
    display: inline-flex;
    align-items: center;
}

.prefix-icon i {
    font-size: 1rem;
}

/* .suffix-icon {
    position: absolute;
    right: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    color: #6c757d;
}

.suffix-icon i {
    font-size: 1rem;
}

.error-icon {
    background: #ffffff;
    border: 2px solid #dc3545;
    color: #dc3545;
    border-radius: 9999px;
    font-weight: 700;
    font-size: 0.9rem;
    line-height: 1rem;
} */

/* Error border highlight */
/* .date-with-icon.has-error :deep(.el-input__wrapper) {
    box-shadow: 0 0 0 1px #dc3545 inset, 0 0 0 0.1rem rgba(220, 53, 69, 0.1);
    border-color: #dc3545;
} */
</style>
